// Per-account, per-session dismiss for the home-feed "Seavien is now on Google Play" strip.
//
// Design: designs/01-now/2026-09-11-android-app-install-promotion-design.md §3.3. Since 2026-09-23 the
// strip returns EVERY SESSION (operator decision): views and taps never retire it, and a dismiss hides it
// for the rest of the browsing session, at most DISMISS_MAX_HOURS. It stays quiet on purpose: many members
// prefer the browser, so the dismiss must be easy and must hold for the session they are in.
//
// "Session" is the tab's sessionStorage, cleared when the tab closes. The hour cap exists because an
// Android Chrome tab can live for days, and a session that never ends would behave like the old snooze.
// The key moved from v2 to v3, and from localStorage to sessionStorage, so a 7-day snooze written by the
// previous rule does not outlive it.
//
// Per-account for the same reason feedbackPromptState's is (a shared browser must not let one account's
// dismissal hide another's strip). Every access is wrapped: blocked storage and malformed JSON read as
// "not dismissed", the correct failure direction for a latch that only ever suppresses.

import {MetaHandler} from "@/main/common/MetaHandler";

const STORAGE_KEY_PREFIX = "seavien.appPromo.play.v3";

/** Hours a dismiss can hide the strip, even inside a session that never ends. */
export const DISMISS_MAX_HOURS = 12;

const DISMISS_MAX_MS = DISMISS_MAX_HOURS * 60 * 60 * 1000;

type AndroidAppPromoStorage = {
    /** Epoch ms of the member's dismiss in this session. */
    dismissedAt?: number;
};

function storageKey(): string {
    const username = MetaHandler.getLoggedInUsername();
    return username ? `${STORAGE_KEY_PREFIX}::${username}` : `${STORAGE_KEY_PREFIX}::anon`;
}

function readStorage(): AndroidAppPromoStorage {
    try {
        const raw = sessionStorage.getItem(storageKey());
        if (!raw) {
            return {};
        }
        const parsed = JSON.parse(raw) as unknown;
        return parsed && typeof parsed === "object" ? (parsed as AndroidAppPromoStorage) : {};
    } catch {
        return {};
    }
}

export const androidAppPromoState = {
    /** Whether a dismiss in this session, inside the last DISMISS_MAX_HOURS, is hiding the strip. */
    isSnoozed(now: number = Date.now()): boolean {
        const {dismissedAt} = readStorage();
        return typeof dismissedAt === "number" && now - dismissedAt >= 0 && now - dismissedAt < DISMISS_MAX_MS;
    },
    dismiss(now: number = Date.now()): void {
        try {
            sessionStorage.setItem(storageKey(), JSON.stringify({dismissedAt: now}));
        } catch {
            // Best-effort. The in-memory UI has already hidden the strip; losing the latch costs one sighting.
        }
    },
    /** Test/diagnostic helper — clears the latch in this session. */
    reset(): void {
        try {
            sessionStorage.removeItem(storageKey());
        } catch {
            // Nothing to clear when storage is unavailable.
        }
    },
};
